import {
  getVoucherPaymentReceipt,
  getVoucherTypes,
  initiateVoucherOrderPayment,
  Vouchers,
  VoucherPaymentReceipt,
  VoucherOrderStatus,
  getPathologyPanelById,
  PathologyPanel,
  VoucherSubType,
  getMemberProfile,
  MemberProfile,
  VoucherServiceTypes,
  applyVoucher,
  oneVoucherPayment,
  OneVoucherPaymentResponse,
} from "@/services/core-api-adapter";
import {
  FeatureToggleIdentifier,
  isFeatureEnabled,
} from "@/services/feature-toggle-adapter";

import { assign, fromPromise, setup } from "xstate";

const MAX_POLLING_TIME_IN_SECONDS =
  Number(import.meta.env.VITE_APP_VOUCHER_MAX_POLLING_TIME) || 180;
const POLLING_INTERVAL_IN_MILLISECONDS =
  Number(import.meta.env.VITE_APP_VOUCHER_POLLING_INTERVAL) || 5000;

interface Context {
  vouchers: Vouchers | undefined;
  selectedVoucherType: any;
  selectedVoucherSubType: any;
  initiatePaymentResponse: any;
  bloodPanelListResponse: PathologyPanel[];
  membersProfileResponse: MemberProfile | null;
  selectedBloodPanel: PathologyPanel | null;
  selectedPromoOrVoucher: any;
  paymentStartTime: Date | null;
  paymentReceiptResponse: VoucherPaymentReceipt | null;
  voucherCode: string | undefined;
  currentUserMemberId: string | null;
  applyVoucherError: any | null;
  orderIdQueryParam: string | null;
  applyVoucherResponse: any | null;
  isMyVouchersQueryParam: string | null;
  isEnhancedPaymentEnabled: boolean;
  oneVoucherPin?: string;
  oneVoucherRedemptionResponse: OneVoucherPaymentResponse | null;
  oneVoucherRedemptionError: any;
}

const initialContextValues: Context = {
  vouchers: undefined,
  selectedVoucherType: null,
  membersProfileResponse: null,
  selectedPromoOrVoucher: null,
  selectedBloodPanel: null,
  bloodPanelListResponse: [],
  selectedVoucherSubType: null,
  applyVoucherResponse: null,
  initiatePaymentResponse: null,
  paymentStartTime: null,
  paymentReceiptResponse: null,
  voucherCode: undefined,
  currentUserMemberId: null,
  applyVoucherError: null,
  orderIdQueryParam: null,
  isMyVouchersQueryParam: null,
  isEnhancedPaymentEnabled: false,
  oneVoucherPin: undefined,
  oneVoucherRedemptionResponse: null,
  oneVoucherRedemptionError: null,
};

export const eventNames = {
  APPLY_VOUCHER_CLICKED: "APPLY_VOUCHER_CLICKED",
  DOCTOR_NURSE_CONSULTATION_VOUCHER_CLICKED:
    "DOCTOR_NURSE_CONSULTATION_VOUCHER_CLICKED",
  PATHOLOGY_VOUCHER_CLICKED: "PATHOLOGY_VOUCHER_CLICKED",
  BACK_BUTTON_CLICKED: "BACK_BUTTON_CLICKED",
  GO_HOME_BUTTON_CLICKED: "GO_HOME_BUTTON_CLICKED",
  USER_REDIRECTED: "USER_REDIRECTED",
  BLOOD_PANEL_CLICKED: "BLOOD_PANEL_CLICKED",
  BUY_NOW_BUTTON_CLICKED: "BUY_NOW_BUTTON_CLICKED",
  CONTINUE_BUTTON_CLICKED: "CONTINUE_BUTTON_CLICKED",
  DOCTOR_VOUCHER_CLICKED: "DOCTOR_VOUCHER_CLICKED",
  DOCTOR_MEDICINE_VOUCHER_CLICKED: "DOCTOR_MEDICINE_VOUCHER_CLICKED",
  NURSE_VOUCHER_CLICKED: "NURSE_VOUCHER_CLICKED",
  PAYMENT_SUCCESS_RECEIVED: "PAYMENT_SUCCESS_RECEIVED",
  VIEW_RECEIPT_BUTTON_CLICKED: "VIEW_RECEIPT_BUTTON_CLICKED",
  BUY_VOUCHER_BUTTON_CLICKED: "BUY_VOUCHER_BUTTON_CLICKED",
  USE_VOUCHER_BUTTON_CLICKED: "USE_VOUCHER_BUTTON_CLICKED",
  SAVE_TO_PROFILE_BUTTON_CLICKED: "SAVE_TO_PROFILE_BUTTON_CLICKED",
  CARD_PAYMENT_BUTTON_CLICKED: "CARD_PAYMENT_BUTTON_CLICKED",
  ONE_VOUCHER_BUTTON_CLICKED: "ONE_VOUCHER_BUTTON_CLICKED",
  ONE_VOUCHER_CONTINUE_BUTTON_CLICKED: "ONE_VOUCHER_CONTINUE_BUTTON_CLICKED",
};

export const voucheringFlowStateMachine = setup({
  types: {
    context: {} as Context,
  },
  actions: {
    addVoucherTypesToContext: assign(({ event }) => {
      return {
        vouchers: event.output,
      };
    }),
    addSelectedVoucherTypeToContext: assign(({ event }) => {
      return {
        selectedVoucherType: event.input,
      };
    }),
    addVoucherSubTypeToContext: assign(({ event }) => {
      return {
        selectedVoucherSubType: event.input,
      };
    }),
    removeVoucherSubTypeFromContext: assign(() => {
      return {
        selectedVoucherSubType: null,
      };
    }),
    addInitiatePaymentResponseToContext: assign(({ event }) => {
      return {
        initiatePaymentResponse: event.output,
      };
    }),
    addBloodPanelListResponseToContext: assign(({ event }) => {
      return {
        bloodPanelListResponse: event.output,
      };
    }),
    addSelectedPromoOrVoucherToContext: assign(({ event }) => {
      return {
        selectedPromoOrVoucher: event.input,
      };
    }),
    addMembersProfileResponseToContext: assign(({ event }) => {
      return {
        membersProfileResponse: event.output,
      };
    }),

    addSelectedBloodPanelToContext: assign(({ event }) => {
      return {
        selectedBloodPanel: event.output,
      };
    }),
    removeSelectedBloodPanelFromContext: assign(() => {
      return {
        selectedBloodPanel: null,
      };
    }),
    addPaymentStartTimeToContext: assign(() => {
      return {
        paymentStartTime: new Date(),
      };
    }),
    addPaymentReceiptResponseToContext: assign(({ event }) => {
      return {
        paymentReceiptResponse: event.output,
        voucherCode: event.output.voucherCode,
      };
    }),
    addApplyVoucherResponseToContext: assign(({ event }) => {
      return {
        applyVoucherResponse: event.output,
      };
    }),
    addVoucherCodeToContext: assign(({ event }) => {
      return {
        voucherCode: event.input,
      };
    }),
    addOneVoucherPinToContext: assign(({ event }) => {
      return {
        oneVoucherPin: event.input,
      };
    }),
    addOneVoucherRedemptionResponseToContext: assign(({ event }) => {
      return {
        oneVoucherRedemptionResponse: event.output,
      };
    }),
    addOneVoucherRedemptionErrorToContext: assign(({ event }) => {
      return {
        oneVoucherRedemptionError: event.error,
      };
    }),
    addApplyVoucherErrorToContext: assign(({ event }) => {
      return {
        applyVoucherError: event.error,
      };
    }),
    clearVoucherInfoFromContext: assign(() => {
      return {
        applyVoucherError: null,
        voucherCode: undefined,
      };
    }),
    clearOneVoucherRedemptionResponse: assign(() => {
      return {
        oneVoucherRedemptionResponse: null,
        oneVoucherRedemptionError: null,
      };
    }),
    getVoucherSubTypeFromSelectedBloodType: assign(({ context }) => {
      const { selectedVoucherType, selectedBloodPanel } = context;

      if (!selectedBloodPanel || !selectedVoucherType) {
        console.error(
          "Either selectedBloodPanel or selectedVoucherType is missing"
        );
        return context;
      }

      const matchingVoucherSubType = selectedVoucherType.voucherSubTypes?.find(
        (subType: any) =>
          subType.chargeCode === selectedBloodPanel.panelChargeCode
      );

      if (!matchingVoucherSubType) {
        console.error(
          `No matching voucher sub-type found for chargeCode: ${selectedBloodPanel.panelChargeCode}`
        );
        return context;
      }

      return {
        ...context,
        selectedVoucherSubType: matchingVoucherSubType,
      };
    }),
  },
  actors: {
    getVoucherTypes: fromPromise(async () => {
      return getVoucherTypes();
    }),

    getBloodPanelById: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        const selectedBloodPanel = input?.context?.selectedBloodPanel;
        if (selectedBloodPanel) {
          const res = await getPathologyPanelById(selectedBloodPanel.panelId);
          return res;
        }
        return null;
      }
    ),

    getMemberProfile: fromPromise(async () => {
      return await getMemberProfile();
    }),

    getBloodPanelList: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        const pathologyPanels = await getPathologyPanelById("");

        return mapSubTypePropsToPathologyPanel(
          pathologyPanels.panels,
          input?.context?.selectedVoucherType?.voucherSubTypes || []
        );
      }
    ),

    initiatePayment: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        let voucherVendorDefinitionId = "";
        let voucherCode = "";

        if (!input?.context?.selectedBloodPanel) {
          voucherVendorDefinitionId =
            input?.context?.selectedVoucherSubType?.voucherVendorDefinitionId ||
            "";
        } else {
          voucherVendorDefinitionId =
            input?.context?.selectedVoucherType?.voucherSubTypes.filter(
              (voucherSubType: any) =>
                voucherSubType.chargeCode ==
                input?.context?.selectedBloodPanel?.panelChargeCode
            )[0].voucherVendorDefinitionId || "";
        }

        if (input?.context?.voucherCode) {
          voucherCode = input?.context?.voucherCode;
        }

        return initiateVoucherOrderPayment(
          voucherVendorDefinitionId,
          voucherCode
        );
      }
    ),

    oneVoucherRedemption: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        return oneVoucherPayment(
          input?.context?.initiatePaymentResponse?.voucherOrderId || "",
          input?.context?.oneVoucherPin || ""
        );
      }
    ),

    fetchPaymentReceipt: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        return getVoucherPaymentReceipt(
          input?.context?.initiatePaymentResponse?.voucherOrderId ||
            input?.context?.orderIdQueryParam
        );
      }
    ),

    applyVoucherCode: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        return applyVoucher(
          input?.context?.voucherCode || "",
          input?.context?.currentUserMemberId || ""
        );
      }
    ),
  },
  guards: {
    timeoutExceeded: function ({ context }) {
      const currentDate = new Date();
      const paymentStartTime = context.paymentStartTime;

      if (paymentStartTime) {
        return (
          Number(currentDate.getTime() - paymentStartTime.getTime()) / 1000 >
          MAX_POLLING_TIME_IN_SECONDS
        );
      }

      return false;
    },
    userHasSuccessfulPayment: function ({ context }) {
      const isPaymentSuccessful =
        Boolean(context.paymentReceiptResponse?.paymentSuccess) ||
        Boolean(context.oneVoucherRedemptionResponse?.success);

      return isPaymentSuccessful;
    },
    userHasUnsuccessfulPayment: function ({ context }) {
      return (
        context.paymentReceiptResponse?.status ===
        VoucherOrderStatus.PAYMENT_FAILED
      );
    },
    hasOrderIdInQueryParams: function ({ context }) {
      return context.orderIdQueryParam !== null;
    },
    hasIsMyVouchersInQueryParams: function ({ context }) {
      return context.isMyVouchersQueryParam !== null;
    },
    isDoctorNurseConsultation: ({ context }) => {
      return (
        context.selectedVoucherType.voucherTypeId ===
        VoucherServiceTypes.CONSULTATION
      );
    },
    isPathologyVoucher: ({ context }) => {
      return (
        context.selectedVoucherType.voucherTypeId ===
        VoucherServiceTypes.PATHOLOGY
      );
    },
    isEnhancedPaymentOptionsEnabled: ({ context }) => {
      return context.isEnhancedPaymentEnabled;
    },
    isOneVoucherRedemptionSuccess: ({ context }) => {
      return !!context.oneVoucherRedemptionResponse?.success;
    },
    isOneVoucherRedemptionErrorMessage: ({ context }) => {
      try {
        return !!JSON.parse(context.oneVoucherRedemptionError);
      } catch {
        return false;
      }
    },
  },
  delays: {
    POLLING_INTERVAL: POLLING_INTERVAL_IN_MILLISECONDS,
  },
}).createMachine({
  context: initialContextValues,
  id: "voucheringFlow",
  initial: "fetchingVoucherTypes",
  states: {
    fetchingVoucherTypes: {
      entry: assign(({ event }) => {
        const entryContext = { ...initialContextValues };
        const { currentUserMemberId = null } = event.input?.context
          ? event.input.context
          : {};
        const { orderId = null } = event.input?.context
          ? event.input.context
          : {};
        const { isMyVouchers = null } = event.input?.context
          ? event.input.context
          : {};

        return {
          ...entryContext,
          currentUserMemberId: currentUserMemberId,
          orderIdQueryParam: orderId,
          isMyVouchersQueryParam: isMyVouchers,
          isEnhancedPaymentEnabled: isFeatureEnabled(
            FeatureToggleIdentifier.ENABLE_ENHANCED_PAYMENT_OPTIONS
          ),
        };
      }),
      always: [
        {
          target: "fetchingPaymentReceipt",
          guard: {
            type: "hasOrderIdInQueryParams",
          },
        },
      ],
      invoke: {
        src: "getVoucherTypes",
        onDone: {
          target: "fetchingMemberProfile",
          actions: "addVoucherTypesToContext",
        },
        onError: {
          target: "error",
        },
      },
    },
    fetchingMemberProfile: {
      invoke: {
        id: "fetchingMemberProfile",
        input: ({ context }) => ({ context }),
        onDone: {
          target: "buyVoucher",
          actions: {
            type: "addMembersProfileResponseToContext",
          },
        },
        onError: {
          target: "error",
        },
        src: "getMemberProfile",
      },
    },
    buyVoucher: {
      entry: "clearVoucherInfoFromContext",
      on: {
        [eventNames.DOCTOR_NURSE_CONSULTATION_VOUCHER_CLICKED]: {
          target: "doctorNurseConsultSelection",
          actions: "addSelectedVoucherTypeToContext",
        },
        [eventNames.PATHOLOGY_VOUCHER_CLICKED]: {
          target: "fetchingBloodPanelList",
          actions: "addSelectedVoucherTypeToContext",
        },
        [eventNames.USE_VOUCHER_BUTTON_CLICKED]: {
          target: "useVoucher",
        },
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    useVoucher: {
      on: {
        [eventNames.APPLY_VOUCHER_CLICKED]: {
          target: "applyingVoucherCode",
          actions: "addVoucherCodeToContext",
        },
        [eventNames.BUY_VOUCHER_BUTTON_CLICKED]: {
          target: "buyVoucher",
        },
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    applyingVoucherCode: {
      invoke: {
        src: "applyVoucherCode",
        input: ({ context }) => ({
          context,
        }),
        onDone: {
          target: "promptingVoucherCode",
          actions: { type: "addApplyVoucherResponseToContext" },
        },
        onError: {
          target: "useVoucher",
          actions: "addApplyVoucherErrorToContext",
        },
      },
    },
    promptingVoucherCode: {
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "useVoucher",
        },
        [eventNames.GO_HOME_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    doctorNurseConsultSelection: {
      on: {
        [eventNames.DOCTOR_VOUCHER_CLICKED]: {
          target: "showingPrompt",
          actions: "addVoucherSubTypeToContext",
        },
        [eventNames.NURSE_VOUCHER_CLICKED]: {
          target: "showingPrompt",
          actions: "addVoucherSubTypeToContext",
        },
        [eventNames.DOCTOR_MEDICINE_VOUCHER_CLICKED]: {
          target: "showingPrompt",
          actions: "addVoucherSubTypeToContext",
        },
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "buyVoucher",
          actions: "removeVoucherSubTypeFromContext",
        },
      },
    },
    showingPrompt: {
      on: {
        [eventNames.CONTINUE_BUTTON_CLICKED]: [
          {
            target: "initiatingPayment",
            actions: "addVoucherCodeToContext",
          },
        ],
        [eventNames.BACK_BUTTON_CLICKED]: [
          {
            target: "doctorNurseConsultSelection",
            guard: "isDoctorNurseConsultation",
          },
          {
            target: "showingBloodPanelDetails",
            guard: "isPathologyVoucher",
          },
        ],
      },
    },
    showingPaymentOptionsScreen: {
      entry: "clearOneVoucherRedemptionResponse",
      on: {
        [eventNames.CARD_PAYMENT_BUTTON_CLICKED]: {
          target: "breakoutScreen",
        },
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "showingPrompt",
        },
        [eventNames.ONE_VOUCHER_BUTTON_CLICKED]: {
          target: "oneVoucherRedemptionScreen",
        },
      },
    },
    initiatingPayment: {
      invoke: {
        input: ({ context }) => ({ context }),
        onDone: [
          {
            target: "showingPaymentOptionsScreen",
            actions: {
              type: "addInitiatePaymentResponseToContext",
            },
            guard: "isEnhancedPaymentOptionsEnabled",
          },
          {
            target: "breakoutScreen",
            actions: {
              type: "addInitiatePaymentResponseToContext",
            },
          },
        ],
        onError: {
          target: "showingPrompt",
          actions: "addApplyVoucherErrorToContext",
        },
        src: "initiatePayment",
      },
    },
    oneVoucherRedemptionScreen: {
      on: {
        [eventNames.ONE_VOUCHER_CONTINUE_BUTTON_CLICKED]: {
          target: "initiatingOneVoucherRedemption",
          actions: "addOneVoucherPinToContext",
        },
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "showingPaymentOptionsScreen",
          guard: "isEnhancedPaymentOptionsEnabled",
        },
      },
    },
    initiatingOneVoucherRedemption: {
      invoke: {
        input: ({ context }) => ({ context }),
        onDone: [
          {
            target: "handlingOneVoucherRedemption",
            actions: {
              type: "addOneVoucherRedemptionResponseToContext",
            },
          },
        ],
        onError: {
          target: "handleInitiatingOneVoucherRedemptionError",
          actions: "addOneVoucherRedemptionErrorToContext",
        },
        src: "oneVoucherRedemption",
      },
    },
    handleInitiatingOneVoucherRedemptionError: {
      always: [
        {
          target: "oneVoucherRedemptionScreen",
          guard: "isOneVoucherRedemptionErrorMessage",
        },
        { target: "error" },
      ],
    },
    handlingOneVoucherRedemption: {
      always: [
        {
          target: "fetchingPaymentReceipt",
          guard: "isOneVoucherRedemptionSuccess",
        },
        {
          target: "oneVoucherRedemptionScreen",
        },
      ],
    },
    breakoutScreen: {
      on: {
        [eventNames.USER_REDIRECTED]: {
          target: "waitingForPaymentOutcome",
          actions: {
            type: "addPaymentStartTimeToContext",
          },
        },
        [eventNames.BACK_BUTTON_CLICKED]: [
          {
            target: "showingPaymentOptionsScreen",
            guard: "isEnhancedPaymentOptionsEnabled",
          },
          {
            target: "showingPrompt",
          },
        ],
      },
    },
    waitingForPaymentOutcome: {
      after: {
        POLLING_INTERVAL: {
          target: "fetchingPaymentReceipt",
        },
      },
    },
    fetchingBloodPanelList: {
      invoke: {
        input: ({ context }) => ({ context }),
        onDone: {
          target: "showingBloodPanelList",
          actions: {
            type: "addBloodPanelListResponseToContext",
          },
        },
        onError: {
          target: "error",
        },
        src: "getBloodPanelList",
      },
    },
    showingBloodPanelList: {
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "buyVoucher",
        },
        [eventNames.BLOOD_PANEL_CLICKED]: {
          target: "fetchingBloodPanelDetails",
          actions: { type: "addSelectedBloodPanelToContext" },
        },
      },
    },
    fetchingBloodPanelDetails: {
      invoke: {
        input: ({ context }) => ({ context }),
        onDone: {
          target: "showingBloodPanelDetails",
          actions: {
            type: "addSelectedBloodPanelToContext",
          },
        },
        onError: {
          target: "error",
        },
        src: "getBloodPanelById",
      },
    },
    showingBloodPanelDetails: {
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: [
          {
            target: "showingBloodPanelList",
            actions: {
              type: "removeSelectedBloodPanelFromContext",
            },
          },
        ],
        [eventNames.BUY_NOW_BUTTON_CLICKED]: {
          target: "showingPrompt",
          actions: {
            type: "getVoucherSubTypeFromSelectedBloodType",
          },
        },
      },
    },
    fetchingPaymentReceipt: {
      invoke: {
        id: "fetchPaymentReceipt",
        input: ({ context }) => ({ context }),
        onDone: {
          target: "processingPaymentOutcome",
          actions: {
            type: "addPaymentReceiptResponseToContext",
          },
        },
        onError: {
          target: "error",
        },
        src: "fetchPaymentReceipt",
      },
    },
    processingPaymentOutcome: {
      always: [
        {
          target: "exit",
          guard: {
            type: "timeoutExceeded",
          },
        },
        {
          target: "showingOrderSummary",
          guard: {
            type: "userHasSuccessfulPayment",
          },
        },
        {
          target: "paymentFailed",
          guard: {
            type: "userHasUnsuccessfulPayment",
          },
        },
        {
          target: "waitingForPaymentOutcome",
        },
      ],
    },
    showingOrderSummary: {
      on: {
        [eventNames.VIEW_RECEIPT_BUTTON_CLICKED]: {
          target: "showingPaymentReceipt",
        },
        [eventNames.BACK_BUTTON_CLICKED]: [
          {
            target: "exitToMyVouchers",
            guard: {
              type: "hasIsMyVouchersInQueryParams",
            },
          },
          {
            target: "exitToMyPurchases",
            guard: {
              type: "hasOrderIdInQueryParams",
            },
          },
          {
            target: "exit",
          },
        ],
        [eventNames.SAVE_TO_PROFILE_BUTTON_CLICKED]: {
          target: "showingAddToProfile",
        },
      },
    },
    showingAddToProfile: {
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "showingOrderSummary",
        },
        [eventNames.CONTINUE_BUTTON_CLICKED]: {
          target: "applyingVoucherCode",
        },
      },
    },
    exitToMyVouchers: {
      type: "final",
    },
    exitToMyPurchases: {
      type: "final",
    },
    showingPaymentReceipt: {
      on: {
        BACK_BUTTON_CLICKED: {
          target: "showingOrderSummary",
        },
      },
    },
    paymentFailed: {
      on: {
        GO_HOME_BUTTON_CLICKED: {
          target: "exit",
        },
      },
    },
    pathologyTestSelection: {},
    exit: {
      type: "final",
    },
    error: {
      on: {
        [eventNames.GO_HOME_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
  },
});

/**
 * Maps the sub type props to the pathology panel.
 * @param pathologyPanels - The pathology panels.
 * @param subtypes - The subtypes of the voucher.
 * @returns The mapped props to the pathology panel.
 */
function mapSubTypePropsToPathologyPanel(
  pathologyPanels: PathologyPanel[],
  subtypes: VoucherSubType[]
): PathologyPanel[] {
  const mappedPathologyPanels = pathologyPanels.map((panel: PathologyPanel) => {
    const matchingSubtype = subtypes.find(
      (subtype: VoucherSubType) => subtype.chargeCode === panel.panelChargeCode
    );

    return {
      ...panel,
      amountTotal: matchingSubtype ? matchingSubtype.amount : 0,
      discountAmount: matchingSubtype ? matchingSubtype.discountAmount : 0,
      originalAmount: matchingSubtype ? matchingSubtype.originalAmount : 0,
      orderIndex: matchingSubtype ? matchingSubtype.order : 0,
    };
  });

  return mappedPathologyPanels;
}
